import * as React from 'react';
import * as Router from 'react-router-dom';
import { A, Hooks, $, $$ } from 'frontend';
import {
  useFolderArtworkSave,
  useFolderArtworkRemove,
  useFolderArtworkDrop,
  useFolderArtworkPaste,
} from 'frontend/hooks/music';
import { getDirName, getPathFileName, getKeyboardString } from 'frontend/utils';
import type { TrackTagsLoadState } from 'frontend/logic/music/metadata';
import type { EmbedFolderArtworkResponse } from 'shared/@types/shared';
import { Tooltip } from 'frontend/components/Tooltip';
import { memoizeLatest } from 'shared/utils';

export interface PendingArtwork {
  data: Blob;
  contentType: string;
  previewUrl: string;
}

interface Props {
  folderArtworkUrl: string | null;
  folderArtworkPath: string | null;
  emptyMessage?: string;
  hideEmbeddedArtwork?: boolean;
  canEditFolderArtwork?: boolean;
  canEmbedFolderArtwork?: boolean;
  tagsState: TrackTagsLoadState;
  trackPath: string;
  embeddableTrackPaths: string[];
  onFolderArtworkWritten: (folderArtworkPath: string) => void;
  onFolderArtworkRemoved?: () => void;
  pendingArtwork: PendingArtwork | null;
  canRedoArtwork: boolean;
  onStageArtwork: (artwork: PendingArtwork) => void;
  onUndoArtwork: () => void;
  onRedoArtwork: () => void;
  onTracksEmbedded: (trackPaths: string[]) => void;
  onEmbeddedArtworkRemoved: (trackPath: string) => void;
  serverUrl: string;
  artistHint?: string;
  albumHint?: string;
}

/**
 * Human-readable byte size, e.g. 240 KB.
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Decoded byte length of a base64 string, ignoring padding.
 */
function base64ByteLength(b64: string): number {
  let length = Math.floor((b64.length * 3) / 4);
  if (b64.endsWith('==')) {
    length -= 2;
  } else if (b64.endsWith('=')) {
    length -= 1;
  }
  return length;
}

/**
 * Join the non-empty parts of a middot-separated metadata run.
 */
function metaLine(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' · ');
}

/**
 * Inline SVG icons. Kept inline (rather than /svg/*.svg files) so they inherit
 * the surrounding text colour via `currentColor`.
 */
function OpenExternalIcon() {
  return (
    <svg
      className="artworkPathLinkIcon"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M18 13v4a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3h4" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={
        open
          ? 'artworkEmbeddedChevron artworkEmbeddedChevron-open'
          : 'artworkEmbeddedChevron'
      }
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function PictureIcon() {
  return (
    <svg
      className="artworkChangeBtnIcon"
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-4.5-4.5L7 21" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      className="artworkItunesSearchBtnIcon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="artworkEmbedBannerIcon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

function EmbedIcon() {
  return (
    <svg
      className="artworkEmbedBannerButtonIcon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v10" />
      <path d="m8 9 4 4 4-4" />
      <path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

interface ArtworkButtonProps {
  trackPath: string;
  serverUrl: string;
  /** Called with the written folder artwork path once a save succeeds. */
  onSaved: (folderArtworkPath: string) => void;
}

function artworkButtonStatus(
  active: boolean,
  saveStatus: ReturnType<typeof $$.getMusicFolderArtworkSaveStatus>,
): string | null {
  if (!active) {
    return null;
  }
  if (saveStatus === 'saving') {
    return 'Saving…';
  }
  if (saveStatus === 'saved') {
    return 'Saved';
  }
  if (saveStatus === 'error') {
    return 'Error — retry';
  }
  return null;
}

function UploadArtworkButton({
  trackPath,
  serverUrl,
  onSaved,
  label = 'Upload artwork',
}: ArtworkButtonProps & { label?: string }) {
  const { saveStatus, active, save } = useFolderArtworkSave(serverUrl, onSaved);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const status = artworkButtonStatus(active, saveStatus);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) {
            save(trackPath, {
              data: file,
              contentType: file.type || 'image/jpeg',
            });
          }
        }}
      />
      <button
        type="button"
        className="artworkChangeBtn"
        disabled={saveStatus === 'saving' || (active && saveStatus === 'saved')}
        onClick={() => inputRef.current?.click()}
      >
        {status ?? (
          <>
            <PictureIcon />
            {label}
          </>
        )}
      </button>
    </>
  );
}

interface ItunesArtworkResult {
  thumbnailUrl: string;
  artworkUrl: string;
  collectionName: string | null;
  artistName: string | null;
}

interface ItunesSearchApiResult {
  artworkUrl100?: string;
  collectionName?: string;
  artistName?: string;
}

function resizeItunesArtworkUrl(url: string, size: number): string {
  return url.replace(/\d+x\d+bb\.jpg$/, `${size}x${size}bb.jpg`);
}

const searchItunesArtwork = memoizeLatest(
  async (term: string): Promise<ItunesArtworkResult[]> => {
    const url =
      'https://itunes.apple.com/search?media=music&entity=album&limit=25' +
      `&term=${encodeURIComponent(term)}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`${res.status}`);
    }
    const data = (await res.json()) as { results: ItunesSearchApiResult[] };
    return data.results
      .filter((entry) => typeof entry.artworkUrl100 === 'string')
      .map((entry) => {
        const artworkUrl100 = entry.artworkUrl100 as string;
        return {
          thumbnailUrl: resizeItunesArtworkUrl(artworkUrl100, 400),
          artworkUrl: resizeItunesArtworkUrl(artworkUrl100, 1200),
          collectionName: entry.collectionName ?? null,
          artistName: entry.artistName ?? null,
        };
      });
  },
  20,
);

const downloadItunesArtwork = memoizeLatest(async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${res.status}`);
  }
  return res.blob();
}, 5);

function useItunesArtworkSearch(initialQuery: string) {
  const [query, setQuery] = React.useState(initialQuery);
  const [status, setStatus] = React.useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  const [results, setResults] = React.useState<ItunesArtworkResult[]>([]);
  const requestId = React.useRef(0);

  const search = React.useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed) {
      setStatus('idle');
      setResults([]);
      return;
    }
    const id = ++requestId.current;
    setStatus('loading');
    searchItunesArtwork(trimmed)
      .then((nextResults) => {
        if (id === requestId.current) {
          setResults(nextResults);
          setStatus('loaded');
        }
      })
      .catch(() => {
        if (id === requestId.current) {
          setStatus('error');
        }
      });
  }, []);

  return { query, setQuery, status, results, search };
}

function ItunesResultSize({ url }: { url: string }) {
  const [size, setSize] = React.useState<string | null>(null);

  React.useEffect(() => {
    const img = new Image();
    img.onload = () => setSize(`${img.naturalWidth} × ${img.naturalHeight}`);
    img.src = url;
    return () => {
      img.onload = null;
    };
  }, [url]);

  return <span className="artworkItunesResultSize">{size ?? '\u00a0'}</span>;
}

function ItunesArtworkSearchPanel({
  initialQuery,
  autoSearch,
  onStage,
}: {
  initialQuery: string;
  autoSearch: boolean;
  onStage: (artwork: PendingArtwork) => void;
}) {
  const dispatch = Hooks.useDispatch();
  const { query, setQuery, status, results, search } =
    useItunesArtworkSearch(initialQuery);
  const [downloadingUrl, setDownloadingUrl] = React.useState<string | null>(
    null,
  );
  const searchedOnMount = React.useRef(false);

  React.useEffect(() => {
    if (!autoSearch || searchedOnMount.current) {
      return;
    }
    searchedOnMount.current = true;
    if (initialQuery.trim()) {
      search(initialQuery);
    }
  }, [autoSearch, initialQuery, search]);

  async function stageResult(result: ItunesArtworkResult) {
    setDownloadingUrl(result.artworkUrl);
    try {
      const blob = await downloadItunesArtwork(result.artworkUrl);
      setDownloadingUrl(null);
      onStage({
        data: blob,
        contentType: blob.type || 'image/jpeg',
        previewUrl: URL.createObjectURL(blob),
      });
    } catch {
      setDownloadingUrl(null);
      dispatch(
        A.addMessage({
          message: 'Could not download that artwork from iTunes.',
          timeout: true,
        }),
      );
    }
  }

  return (
    <div className="artworkItunesSearch">
      <form
        className="artworkItunesSearchForm"
        onSubmit={(event) => {
          event.preventDefault();
          search(query);
        }}
      >
        <input
          className="artworkItunesSearchInput"
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Artist and album"
        />
        <button
          type="submit"
          className="artworkItunesSearchBtn"
          disabled={status === 'loading' || !query.trim()}
        >
          <SearchIcon />
          {status === 'loading' ? 'Searching…' : 'Search iTunes for artwork'}
        </button>
      </form>
      {status === 'error' && (
        <div className="artworkSectionError">
          Couldn’t reach iTunes.{' '}
          <button
            type="button"
            className="artworkSaveFolderBtn"
            onClick={() => search(query)}
          >
            Retry
          </button>
        </div>
      )}
      {status === 'loaded' && results.length === 0 && (
        <div className="artworkMetaLine">No matches on iTunes.</div>
      )}
      {results.length > 0 && (
        <div className="artworkItunesResultsGrid">
          {results.map((result) => (
            <button
              key={result.artworkUrl}
              type="button"
              className="artworkItunesResult"
              disabled={downloadingUrl !== null}
              title={metaLine([result.collectionName, result.artistName])}
              onClick={() => stageResult(result)}
            >
              <img
                className="artworkItunesResultImg"
                src={result.thumbnailUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
              <span className="artworkItunesResultLabel">
                {metaLine([result.collectionName, result.artistName])}
              </span>
              <ItunesResultSize url={result.artworkUrl} />
              {downloadingUrl === result.artworkUrl && (
                <span className="artworkItunesResultOverlay">Loading…</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RemoveFolderArtworkButton({
  trackPath,
  serverUrl,
  onRemoved,
}: {
  trackPath: string;
  serverUrl: string;
  onRemoved?: () => void;
}) {
  const { removeStatus, active, remove } = useFolderArtworkRemove(
    serverUrl,
    onRemoved,
  );
  const [confirming, setConfirming] = React.useState(false);
  const busy =
    active && (removeStatus === 'saving' || removeStatus === 'saved');

  let label: React.ReactNode = (
    <>
      <span className="artworkEmbeddedRemoveBtnIcon" aria-hidden="true" />
      Remove artwork
    </>
  );
  if (active && removeStatus === 'saving') {
    label = 'Removing…';
  } else if (active && removeStatus === 'saved') {
    label = 'Removed';
  } else if (active && removeStatus === 'error') {
    label = 'Error — retry';
  } else if (confirming) {
    label = 'Click to confirm';
  }

  return (
    <button
      type="button"
      className={
        confirming
          ? 'artworkChangeBtn artworkChangeBtnDanger artworkChangeBtnDangerConfirm'
          : 'artworkChangeBtn artworkChangeBtnDanger'
      }
      disabled={busy}
      onClick={() => {
        if (busy) {
          return;
        }
        if (confirming || (active && removeStatus === 'error')) {
          setConfirming(false);
          remove(trackPath);
        } else {
          setConfirming(true);
        }
      }}
      onBlur={() => setConfirming(false)}
    >
      {label}
    </button>
  );
}

function SetAsAlbumArtworkButton({
  trackPath,
  serverUrl,
  onSaved,
  format,
}: ArtworkButtonProps & { format: string }) {
  const { saveStatus, active, save } = useFolderArtworkSave(serverUrl, onSaved);
  const status = artworkButtonStatus(active, saveStatus);
  const targetFile = format === 'image/png' ? 'Folder.png' : 'Folder.jpg';

  return (
    <button
      type="button"
      className="artworkSaveFolderBtn"
      disabled={saveStatus === 'saving' || (active && saveStatus === 'saved')}
      onClick={() => save(trackPath)}
    >
      {status ?? `Set as ${targetFile} artwork`}
    </button>
  );
}

function useRemoveEmbeddedArtwork(
  serverUrl: string,
  onRemoved: (trackPath: string) => void,
) {
  const dispatch = Hooks.useDispatch();
  const removeStatus = $$.getMusicEmbeddedArtworkRemoveStatus();
  const [active, setActive] = React.useState(false);

  React.useEffect(() => {
    if (removeStatus === 'idle') {
      setActive(false);
    }
  }, [removeStatus]);

  const remove = React.useCallback(
    (path: string) => {
      setActive(true);
      dispatch(A.musicEmbeddedArtworkRemoveStart());
      fetch(
        `${serverUrl}/music/artwork/embedded/remove?path=${encodeURIComponent(path)}`,
        { method: 'POST' },
      )
        .then((res) => {
          if (!res.ok) {
            return res.text().then((t) => {
              throw new Error(t || `${res.status}`);
            });
          }
          return res.json();
        })
        .then(() => {
          onRemoved(path);
          dispatch(A.musicEmbeddedArtworkRemoveSuccess());
        })
        .catch(() => dispatch(A.musicEmbeddedArtworkRemoveError()));
    },
    [dispatch, serverUrl, onRemoved],
  );

  return { removeStatus, active, remove };
}

function RemoveEmbeddedArtworkButton({
  trackPath,
  serverUrl,
  onRemoved,
}: {
  trackPath: string;
  serverUrl: string;
  onRemoved: (trackPath: string) => void;
}) {
  const { removeStatus, active, remove } = useRemoveEmbeddedArtwork(
    serverUrl,
    onRemoved,
  );
  const [confirming, setConfirming] = React.useState(false);
  const busy =
    active && (removeStatus === 'saving' || removeStatus === 'saved');

  let label: React.ReactNode = (
    <>
      <span className="artworkEmbeddedRemoveBtnIcon" aria-hidden="true" />
      Remove
    </>
  );
  if (active && removeStatus === 'saving') {
    label = 'Removing…';
  } else if (active && removeStatus === 'saved') {
    label = 'Removed';
  } else if (active && removeStatus === 'error') {
    label = 'Error — retry';
  } else if (confirming) {
    label = 'Click to confirm';
  }

  return (
    <button
      type="button"
      className={
        confirming
          ? 'artworkEmbeddedRemoveBtn artworkEmbeddedRemoveBtnConfirm'
          : 'artworkEmbeddedRemoveBtn'
      }
      disabled={busy}
      onClick={() => {
        if (busy) {
          return;
        }
        if (confirming || (active && removeStatus === 'error')) {
          setConfirming(false);
          remove(trackPath);
        } else {
          setConfirming(true);
        }
      }}
      onBlur={() => setConfirming(false)}
    >
      {label}
    </button>
  );
}

function EmbedArtworkBanner({
  folderArtworkPath,
  trackPaths,
  serverUrl,
  onEmbedded,
}: {
  folderArtworkPath: string;
  trackPaths: string[];
  serverUrl: string;
  onEmbedded: (trackPaths: string[]) => void;
}) {
  const dispatch = Hooks.useDispatch();
  const embedStatus = $$.getMusicFolderArtworkEmbedStatus();
  // embedStatus lives in the store and outlives this banner, which unmounts
  // once every track carries the art. Gate it behind a mount-local flag so a
  // later remount (e.g. after the art is removed) doesn't replay a stale
  // "saved" and leave the button disabled.
  const [active, setActive] = React.useState(false);
  const count = trackPaths.length;
  const tracksLabel = count === 1 ? 'this track' : `${count} tracks`;

  const status = active ? embedStatus : 'idle';

  function embed() {
    setActive(true);
    dispatch(A.musicFolderArtworkEmbedStart());
    fetch(`${serverUrl}/music/artwork/embed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderArtworkPath, trackPaths }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((t) => {
            throw new Error(t || `${res.status}`);
          });
        }
        return res.json() as Promise<EmbedFolderArtworkResponse>;
      })
      .then((data) => {
        if (data.updated.length > 0) {
          onEmbedded(data.updated);
        }
        if (data.updated.length === 0 && data.errors.length > 0) {
          dispatch(A.musicFolderArtworkEmbedError());
        } else {
          dispatch(A.musicFolderArtworkEmbedSuccess());
        }
      })
      .catch(() => dispatch(A.musicFolderArtworkEmbedError()));
  }

  let buttonLabel: React.ReactNode = (
    <>
      <EmbedIcon />
      Embed
    </>
  );
  if (status === 'saving') {
    buttonLabel = 'Embedding…';
  } else if (status === 'saved') {
    buttonLabel = 'Embedded';
  } else if (status === 'error') {
    buttonLabel = 'Error — retry';
  }

  return (
    <div className="artworkEmbedBanner">
      <div className="artworkEmbedBannerHeader">
        <Tooltip text="Embedding it keeps each file portable, so another player or app can show the artwork without this folder.">
          <span
            className="artworkEmbedBannerInfo"
            tabIndex={0}
            aria-label="Why embed artwork"
          >
            <InfoIcon />
          </span>
        </Tooltip>
        <strong>The artwork isn’t embedded inside {tracksLabel}.</strong>
      </div>
      <button
        type="button"
        className="artworkEmbedBannerButton"
        aria-label="Embed artwork"
        disabled={status === 'saving' || status === 'saved'}
        onClick={embed}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function SizedArtworkImage({
  src,
  alt,
  onLoad,
  onError,
}: {
  src: string;
  alt?: string;
  onLoad?: (img: HTMLImageElement) => void;
  onError?: () => void;
}) {
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <img
      className={
        loaded
          ? 'artworkSectionImage'
          : 'artworkSectionImage artworkSectionImageLoading'
      }
      src={src}
      alt={alt}
      onLoad={(event) => {
        setLoaded(true);
        onLoad?.(event.currentTarget);
      }}
      onError={onError}
    />
  );
}

function ArtworkDropZone({
  trackPath,
  serverUrl,
  canEdit,
  onSaved,
  children,
}: {
  trackPath: string;
  serverUrl: string;
  canEdit: boolean;
  onSaved: (folderArtworkPath: string) => void;
  children: React.ReactNode;
}) {
  const { ref, dragging } = useFolderArtworkDrop({
    trackPath,
    serverUrl,
    canEdit,
    onSaved,
  });

  return (
    <div ref={ref} className="artworkAlbumImageWrap">
      {children}
      {dragging && (
        <div className="artworkDropOverlay" aria-hidden="true">
          Set the album artwork
        </div>
      )}
    </div>
  );
}

function AlbumArtwork({
  src,
  isPending,
  fileName,
  dirName,
  dirHref,
  onOpenDir,
  trackPath,
  serverUrl,
  canEditFolderArtwork,
  onFolderArtworkWritten,
  banner,
  children,
}: {
  src: string;
  isPending: boolean;
  fileName: string;
  dirName: string;
  dirHref: string | null;
  onOpenDir: () => void;
  trackPath: string;
  serverUrl: string;
  canEditFolderArtwork: boolean;
  onFolderArtworkWritten: (folderArtworkPath: string) => void;
  banner?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const [dimensions, setDimensions] = React.useState<string | null>(null);
  const [sizeBytes, setSizeBytes] = React.useState<number | null>(null);
  const [imgError, setImgError] = React.useState(false);

  // Send a HEAD request for the Content-Length so the meta line can show
  // "· 240 KB". This is best effort, so the size segment is dropped if the
  // request fails.
  React.useEffect(() => {
    let cancelled = false;
    setSizeBytes(null);
    setDimensions(null);
    fetch(src, { method: 'HEAD' })
      .then((res) => {
        const header = res.ok ? res.headers.get('content-length') : null;
        const parsed = header ? parseInt(header, 10) : NaN;
        if (!cancelled && Number.isFinite(parsed) && parsed > 0) {
          setSizeBytes(parsed);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <div className="artworkAlbumLayout">
      <ArtworkDropZone
        trackPath={trackPath}
        serverUrl={serverUrl}
        canEdit={canEditFolderArtwork}
        onSaved={onFolderArtworkWritten}
      >
        {imgError ? (
          <div className="artworkSectionError">Unable to load image</div>
        ) : (
          <SizedArtworkImage
            src={src}
            onLoad={(img) =>
              setDimensions(`${img.naturalWidth} × ${img.naturalHeight}`)
            }
            onError={() => setImgError(true)}
          />
        )}
      </ArtworkDropZone>
      <div className="artworkAlbumInfo">
        <div className="artworkAlbumHeading">Album artwork</div>
        <div className="artworkMetaLine">
          {metaLine([
            isPending ? 'Unsaved selection' : fileName,
            dimensions,
            sizeBytes ? formatBytes(sizeBytes) : null,
          ])}
        </div>
        {dirHref && (
          <a
            className="artworkPathLink"
            href={dirHref}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                return;
              }
              e.preventDefault();
              onOpenDir();
            }}
          >
            <span className="artworkPathLinkText">{dirName}</span>
            <OpenExternalIcon />
          </a>
        )}
        {children && <div className="artworkAlbumActions">{children}</div>}
        {banner}
      </div>
    </div>
  );
}

function EmbeddedArtworkRow({
  src,
  format,
  pictureType,
  sizeBytes,
  trackPath,
  serverUrl,
  canEditFolderArtwork,
  onSetAsAlbumArtwork,
  onRemoved,
}: {
  src: string;
  format: string;
  pictureType: string;
  sizeBytes: number;
  trackPath: string;
  serverUrl: string;
  canEditFolderArtwork: boolean;
  onSetAsAlbumArtwork: (folderArtworkPath: string) => void;
  onRemoved: (trackPath: string) => void;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [dimensions, setDimensions] = React.useState<string | null>(null);
  const [imgError, setImgError] = React.useState(false);

  return (
    <div className="artworkEmbeddedRow">
      <button
        type="button"
        className="artworkEmbeddedRowToggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <ChevronIcon open={expanded} />
        <span className="artworkEmbeddedThumb">
          {!imgError && (
            <img
              src={src}
              alt=""
              onLoad={(event) => {
                const img = event.currentTarget;
                setDimensions(`${img.naturalWidth} × ${img.naturalHeight}`);
              }}
              onError={() => setImgError(true)}
            />
          )}
        </span>
        <span className="artworkEmbeddedInfo">
          <span className="artworkMetaLine">
            {metaLine([
              format || 'image',
              dimensions,
              sizeBytes > 0 ? formatBytes(sizeBytes) : null,
            ])}
          </span>
          <span className="artworkEmbeddedSub">Embedded in File</span>
        </span>
      </button>
      {expanded && (
        <div className="artworkEmbeddedExpanded">
          <div className="artworkEmbeddedExpandedActions">
            {canEditFolderArtwork && (
              <SetAsAlbumArtworkButton
                trackPath={trackPath}
                serverUrl={serverUrl}
                format={format}
                onSaved={onSetAsAlbumArtwork}
              />
            )}
            <RemoveEmbeddedArtworkButton
              trackPath={trackPath}
              serverUrl={serverUrl}
              onRemoved={onRemoved}
            />
          </div>
          {!imgError && (
            <img className="artworkSectionImage" src={src} alt="" />
          )}
          {pictureType && (
            <div className="artworkEmbeddedSub">{pictureType}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function ArtworkTab({
  folderArtworkUrl,
  folderArtworkPath,
  emptyMessage,
  hideEmbeddedArtwork = false,
  canEditFolderArtwork = false,
  canEmbedFolderArtwork = canEditFolderArtwork,
  tagsState,
  trackPath,
  embeddableTrackPaths,
  onFolderArtworkWritten,
  onFolderArtworkRemoved,
  pendingArtwork,
  canRedoArtwork,
  onStageArtwork,
  onUndoArtwork,
  onRedoArtwork,
  onTracksEmbedded,
  onEmbeddedArtworkRemoved,
  serverUrl,
  artistHint,
  albumHint,
}: Props) {
  const dispatch = Hooks.useDispatch();
  const { getState } = Hooks.useStore();
  const navigate = Router.useNavigate();
  const version = $$.getMusicFolderArtworkVersion();
  const activeTab = $$.getMusicEditTab();
  const itunesInitialQuery =
    artistHint?.trim().toLowerCase() === albumHint?.trim().toLowerCase()
      ? (albumHint ?? '')
      : [artistHint, albumHint].filter(Boolean).join(' ');

  useFolderArtworkPaste({
    trackPath,
    canEdit: canEditFolderArtwork,
    isActive: () => activeTab === 'artwork',
    onPasteImage: (blob) =>
      onStageArtwork({
        data: blob,
        contentType: blob.type || 'image/jpeg',
        previewUrl: URL.createObjectURL(blob),
      }),
  });

  const hasPendingArtwork = pendingArtwork !== null;
  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) {
        return;
      }
      switch (getKeyboardString(event)) {
        case 'Meta+Z':
        case 'Control+Z':
          if (hasPendingArtwork) {
            event.preventDefault();
            onUndoArtwork();
          }
          break;
        case 'Meta+Shift+Z':
        case 'Control+Shift+Z':
          if (canRedoArtwork) {
            event.preventDefault();
            onRedoArtwork();
          }
          break;
        default:
      }
    }
    if ((hasPendingArtwork || canRedoArtwork) && activeTab === 'artwork') {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    hasPendingArtwork,
    canRedoArtwork,
    activeTab,
    onUndoArtwork,
    onRedoArtwork,
  ]);

  const embeddedArtwork = React.useMemo(() => {
    if (hideEmbeddedArtwork) {
      return [];
    }
    if (tagsState.status !== 'loaded') {
      return [];
    }
    return tagsState.data.blocks
      .flatMap((block) => block.tags)
      .filter(
        (tag) =>
          (tag.id === 'APIC' || tag.id === 'covr') && tag.binary !== undefined,
      )
      .map((tag) => ({ value: tag.value, binary: tag.binary! }))
      .filter((entry) => !entry.value.startsWith('-->'));
  }, [hideEmbeddedArtwork, tagsState]);

  const navigateToFile = React.useCallback(
    (filePath: string) => {
      const fsSlug = $.getCurrentFileStoreSlug(getState());
      const folderPath = getDirName(filePath);
      const fileName = getPathFileName(filePath);
      dispatch(A.changeFileFocus(folderPath, fileName));
      navigate(`/${fsSlug}/folder${folderPath}`);
    },
    [dispatch, getState, navigate],
  );

  const folderArtworkHref = React.useMemo(() => {
    if (!folderArtworkPath) {
      return null;
    }
    const fsSlug = $.getCurrentFileStoreSlug(getState());
    return `/${fsSlug}/folder${getDirName(folderArtworkPath)}`;
  }, [folderArtworkPath, getState]);

  if (!folderArtworkUrl && tagsState.status === 'loading') {
    return (
      <div className="editTrackModalArtwork">
        <div className="editTrackModalArtworkEmpty">Loading…</div>
      </div>
    );
  }

  if (
    !folderArtworkUrl &&
    (embeddedArtwork.length === 0 || tagsState.status === 'error')
  ) {
    return (
      <div className="editTrackModalArtworkSections">
        <div className="artworkBlock">
          <div className="artworkBlockLabel">Album artwork</div>
          <div className="artworkAlbumLayout">
            <ArtworkDropZone
              trackPath={trackPath}
              serverUrl={serverUrl}
              canEdit={canEditFolderArtwork}
              onSaved={onFolderArtworkWritten}
            >
              {pendingArtwork ? (
                <SizedArtworkImage
                  src={pendingArtwork.previewUrl}
                  alt="Selected artwork"
                />
              ) : (
                <div className="artworkAlbumPlaceholder" aria-hidden="true" />
              )}
            </ArtworkDropZone>
            <div className="artworkAlbumInfo">
              <div className="artworkMetaLine">
                {pendingArtwork
                  ? 'Unsaved selection'
                  : (emptyMessage ?? 'No artwork found')}
              </div>
              {canEditFolderArtwork && (
                <div className="artworkAlbumActions">
                  <UploadArtworkButton
                    trackPath={trackPath}
                    serverUrl={serverUrl}
                    onSaved={onFolderArtworkWritten}
                    label="Add artwork file"
                  />
                  {pendingArtwork && (
                    <button
                      type="button"
                      className="artworkChangeBtn artworkChangeBtnDanger"
                      onClick={onUndoArtwork}
                    >
                      <span
                        className="artworkEmbeddedRemoveBtnIcon"
                        aria-hidden="true"
                      />
                      Remove artwork
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {canEditFolderArtwork && (
            <ItunesArtworkSearchPanel
              key={trackPath}
              initialQuery={itunesInitialQuery}
              autoSearch={activeTab === 'artwork' && !emptyMessage}
              onStage={onStageArtwork}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="editTrackModalArtworkSections">
      {folderArtworkUrl && folderArtworkPath && (
        <div className="artworkBlock">
          <div className="artworkBlockLabel">Album artwork</div>
          <AlbumArtwork
            src={
              pendingArtwork?.previewUrl ??
              (version ? `${folderArtworkUrl}&v=${version}` : folderArtworkUrl)
            }
            isPending={pendingArtwork !== null}
            fileName={getPathFileName(folderArtworkPath)}
            dirName={`${getDirName(folderArtworkPath)}/`}
            dirHref={folderArtworkHref}
            onOpenDir={() => navigateToFile(folderArtworkPath)}
            trackPath={trackPath}
            serverUrl={serverUrl}
            canEditFolderArtwork={canEditFolderArtwork}
            onFolderArtworkWritten={onFolderArtworkWritten}
            banner={
              canEmbedFolderArtwork && embeddableTrackPaths.length > 0 ? (
                <EmbedArtworkBanner
                  folderArtworkPath={folderArtworkPath}
                  trackPaths={embeddableTrackPaths}
                  onEmbedded={onTracksEmbedded}
                  serverUrl={serverUrl}
                />
              ) : null
            }
          >
            {canEditFolderArtwork && (
              <>
                <UploadArtworkButton
                  trackPath={trackPath}
                  serverUrl={serverUrl}
                  onSaved={onFolderArtworkWritten}
                />
                <RemoveFolderArtworkButton
                  trackPath={trackPath}
                  serverUrl={serverUrl}
                  onRemoved={onFolderArtworkRemoved}
                />
              </>
            )}
          </AlbumArtwork>
          {canEditFolderArtwork && (
            <ItunesArtworkSearchPanel
              key={trackPath}
              initialQuery={itunesInitialQuery}
              autoSearch={false}
              onStage={onStageArtwork}
            />
          )}
        </div>
      )}
      {!hideEmbeddedArtwork && embeddedArtwork.length > 0 && (
        <>
          <div className="artworkDivider" />
          <div className="artworkBlock">
            <div className="artworkBlockLabel">Embedded in this file</div>
            <div className="artworkEmbeddedList">
              {embeddedArtwork.map((entry, i) => {
                const parts = entry.value.split(' — ');
                const rawMime = parts[0] ?? '';
                const pictureType = parts[1] ?? '';
                const mimeType = rawMime.startsWith('image/')
                  ? rawMime
                  : 'image/jpeg';
                const src = `data:${mimeType};base64,${entry.binary}`;
                return (
                  <EmbeddedArtworkRow
                    key={i}
                    src={src}
                    format={rawMime}
                    pictureType={pictureType}
                    sizeBytes={base64ByteLength(entry.binary)}
                    trackPath={trackPath}
                    serverUrl={serverUrl}
                    canEditFolderArtwork={canEditFolderArtwork}
                    onSetAsAlbumArtwork={onFolderArtworkWritten}
                    onRemoved={onEmbeddedArtworkRemoved}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
